import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

/** What the server allows for tools and MCP servers (forms disable options accordingly). */
export interface ToolCapabilities {
  cliToolAllowlist: string[];
  mcpStdioAllowlist: string[];
  claudeBuiltinToolsAllowed: string[];
}

export const useToolCapabilities = (tenantId: string) =>
  useQuery({
    queryKey: ['tool-capabilities', tenantId],
    queryFn: () => api<ToolCapabilities>('/tools/capabilities'),
  });

/** The field-level errors of a failed request, listed by path. */
export function FieldErrors({ error }: { error: unknown }) {
  if (!(error instanceof ApiError) || error.errors.length === 0) return null;
  return (
    <ul className="text-destructive list-disc pl-5 text-sm">
      {error.errors.map((e) => (
        <li key={e.path + e.message}>
          <span className="font-mono">{e.path}</span>: {e.message}
        </li>
      ))}
    </ul>
  );
}

/** JSON with object keys sorted, so equal values compare equal whatever their key order. */
function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
      : v,
  );
}

/** The entries of `next` whose values differ from `current` (an edit form's PATCH body). */
export function changedFields<T extends Record<string, unknown>>(
  next: T,
  current: Partial<Record<keyof T, unknown>>,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) => stableJson(value) !== stableJson(current[key as keyof T]),
    ),
  ) as Partial<T>;
}
